/* Erickson cart-note persistence. Keep note writes separate from native line changes. */
(() => {
  'use strict';
  const selector = '[data-almila-cart-note]';
  let edited = false;
  let latest = '';
  let saved = '';
  let pending = null;
  let timer;
  let failed = false;
  let errorMessage = '';
  let submitting = false; // the native cart submit is on its way; let it leave without a prompt
  // A stalled save must not hold the cart form forever: it fails after this long and the customer can retry.
  const SAVE_TIMEOUT = 10000;

  function timeoutSignal() {
    if (AbortSignal.timeout) return AbortSignal.timeout(SAVE_TIMEOUT);
    const controller = new AbortController();
    setTimeout(() => controller.abort(), SAVE_TIMEOUT);
    return controller.signal;
  }

  function dirty() { return edited && (failed || latest !== saved); }

  function restore() {
    document.querySelectorAll(selector).forEach(field => {
      if (edited && field.value !== latest) field.value = latest;
      const group = field.parentElement;
      const status = group.querySelector('[data-almila-note-status]');
      const retry = group.querySelector('[data-almila-note-retry]');
      if (status) {
        status.textContent = failed ? errorMessage : '';
        status.hidden = !failed;
      }
      if (retry) retry.hidden = !failed;
      if (failed) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
    });
  }

  function flush() {
    clearTimeout(timer);
    if (pending) return pending.then(flush);
    if (!dirty()) return Promise.resolve();
    const value = latest;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    pending = fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: value }),
      keepalive: true,
      signal: timeoutSignal()
    }).then(response => {
      if (!response.ok) throw new Error('Cart note save ' + response.status);
      return response.json();
    }).then(cart => {
      if (cart.note !== value && !(value === '' && cart.note === null)) {
        throw new Error('Cart note was not confirmed');
      }
      saved = value;
      failed = false;
      restore();
    }).catch(error => {
      failed = true;
      restore();
      // A last-item removal may have removed both note controls while saving.
      if (!document.querySelector(selector)) window.alert(errorMessage);
      throw error;
    }).finally(() => { pending = null; });
    return pending.then(flush);
  }

  function save() { flush().catch(() => {}); }

  document.addEventListener('input', event => {
    const field = event.target;
    if (!field.matches(selector)) return;
    if (!edited) saved = field.defaultValue;
    edited = true;
    latest = field.value;
    errorMessage = field.dataset.saveError;
    restore();
    clearTimeout(timer);
    timer = setTimeout(save, 300);
  });
  document.addEventListener('change', event => {
    if (event.target.matches(selector)) save();
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-almila-note-retry]')) return;
    event.preventDefault();
    save();
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (submitting || !form.matches('[data-almila-cart-form]') || (!dirty() && !pending)) return;
    event.preventDefault();
    const submitter = event.submitter;
    // The note field is part of this form, so a failed save still reaches Shopify with the native submit.
    flush().catch(() => {}).then(() => {
      // A native refresh can replace this form while the save is pending.
      if (!form.isConnected) return;
      submitting = true;
      form.requestSubmit(submitter || undefined);
    });
  });
  // Back from checkout (back/forward cache): the page is live again.
  window.addEventListener('pageshow', () => { submitting = false; });
  window.addEventListener('beforeunload', event => {
    if (submitting || (!dirty() && !pending)) return;
    save();
    event.preventDefault();
    event.returnValue = '';
  });

  window.almilaCartNote = {
    restore,
    change: async (url, options) => {
      // A failed save keeps the typed note in memory (restore puts it back after the refresh) and shows Retry;
      // it must not stop the quantity or removal the customer asked for.
      await flush().catch(() => {});
      const before = latest;
      const response = await fetch(url, options);
      // Include edits made while the native quantity/removal request was pending.
      // A failed note save must not suppress refresh after the line already changed.
      if (latest !== before) await flush().catch(() => {});
      return response;
    }
  };
})();
